import {
  collection,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  ACCION_CATEGORIA_MAP,
  type TipoAccionAuditoria,
  type CategoriaAccion,
} from '../types';

const COLLECTION_NAME = 'auditoria_accesos';

interface RegistroAccesoInput {
  uid: string;
  email: string;
  rol: string;
  accion: TipoAccionAuditoria;
  pagina: string;
  descripcion: string;
  detalles?: Record<string, unknown>;
}

/**
 * Registra un evento de acceso/acción en Firestore.
 * Escribe a la colección `auditoria_accesos` con timestamp del servidor.
 */
export async function registrarAcceso(input: RegistroAccesoInput): Promise<void> {
  try {
    const categoria: CategoriaAccion =
      ACCION_CATEGORIA_MAP[input.accion] ?? 'otro';

    const docData = {
      uid: input.uid,
      email: input.email,
      rol: input.rol,
      accion: input.accion,
      categoria,
      pagina: input.pagina,
      descripcion: input.descripcion,
      detalles: input.detalles ?? {},
      userAgent: navigator.userAgent,
      creadoEn: serverTimestamp(),
    };

    await addDoc(collection(db, COLLECTION_NAME), docData);
  } catch (error) {
    // Log visible pero no propagar para no afectar la UX
    console.warn(
      '[AuditoriaAccesos] No se pudo registrar evento:',
      input.accion,
      input.pagina,
      error,
    );
  }
}

/**
 * Helpers pre-configurados para tipos comunes de registro.
 */
export const AuditoriaAccesos = {
  /** Registra una visita a página */
  pageView: (uid: string, email: string, rol: string, pagina: string) =>
    registrarAcceso({
      uid,
      email,
      rol,
      accion: 'PAGE_VIEW',
      pagina,
      descripcion: `Visito la pagina ${pagina}`,
    }),

  /** Registra inicio de sesión */
  login: (uid: string, email: string, rol: string, metodo: string) =>
    registrarAcceso({
      uid,
      email,
      rol,
      accion: 'LOGIN',
      pagina: '/login',
      descripcion: `Inicio de sesion via ${metodo}`,
      detalles: { metodo },
    }),

  /** Registra cierre de sesión */
  logout: (uid: string, email: string, rol: string) =>
    registrarAcceso({
      uid,
      email,
      rol,
      accion: 'LOGOUT',
      pagina: '/',
      descripcion: 'Cerro sesion',
    }),

  /** Registra inicio de consulta */
  consultaIniciada: (
    uid: string,
    email: string,
    rol: string,
    consultaId: string,
    salaId: string,
  ) =>
    registrarAcceso({
      uid,
      email,
      rol,
      accion: 'CONSULTA_INICIADA',
      pagina: `/consulta/${consultaId}/video`,
      descripcion: `Inicio consulta ${consultaId} en sala ${salaId}`,
      detalles: { consultaId, salaId },
    }),

  /** Registra finalización de consulta */
  consultaFinalizada: (
    uid: string,
    email: string,
    rol: string,
    consultaId: string,
    disposicion: string,
  ) =>
    registrarAcceso({
      uid,
      email,
      rol,
      accion: 'CONSULTA_FINALIZADA',
      pagina: `/consulta/${consultaId}/resumen`,
      descripcion: `Finalizo consulta ${consultaId} (${disposicion})`,
      detalles: { consultaId, disposicion },
    }),

  /** Registra una acción administrativa */
  accionAdmin: (
    uid: string,
    email: string,
    rol: string,
    accion: TipoAccionAuditoria,
    pagina: string,
    descripcion: string,
    detalles?: Record<string, unknown>,
  ) =>
    registrarAcceso({ uid, email, rol, accion, pagina, descripcion, detalles }),

  /** Registra una acción genérica */
  accionCustom: (
    uid: string,
    email: string,
    rol: string,
    pagina: string,
    descripcion: string,
    detalles?: Record<string, unknown>,
  ) =>
    registrarAcceso({
      uid,
      email,
      rol,
      accion: 'ACCION_CUSTOM',
      pagina,
      descripcion,
      detalles,
    }),
};
